import EChart from "components/Charts/ECharts/EChart.component";
import InfoMessage from "components/Commons/InfoMessage";
import { useMemo, useRef, useState } from "react";
import { IconButton } from "components/form/IconButton/IconButton";
import { logWhenInDevMode } from "utils/debug";
import { EChartsOption } from "echarts";
import './AnalyticsChart.scss';

export type AnalyticsChartProps = {
  data: EChartsOption;
  divergenceNote?: string | null;
  chartId?: string;
  onDelete?: (chartId: string) => void;
};

const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ data, divergenceNote, chartId, onDelete }) => {
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  const ECHART_PALETTE = ['#5470c6','#91cc75','#fac858','#ee6666','#73c0de','#3ba272','#fc8452','#9a60b4','#ea7ccc'];

  const seriesInfo = useMemo(() => {
    const series = Array.isArray(data.series) ? data.series : [data.series];
    return series
      .filter((s: any): s is any => Boolean(s?.name))
      .map((s: any, idx: number) => ({
        name: s.name as string,
        color: (s.itemStyle?.color as string) || ECHART_PALETTE[idx % ECHART_PALETTE.length],
        dashed: s.lineStyle?.type === 'dashed',
      }));
  }, [data.series]);

  const seriesNames = seriesInfo.map(s => s.name);

  const toggleSeries = (name: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const titleOption = Array.isArray(data.title) ? data.title[0] : data.title;
  const hasTitle = titleOption && (typeof titleOption === 'string' || (titleOption as any).text);
  const hasSubtitle = titleOption && typeof titleOption === 'object' && (titleOption as any).subtext;

  let topSpacing = 20;
  if (hasTitle) {
    topSpacing = hasSubtitle ? 120 : 80;
  }

  // Check if this is a scatter chart
  const isScatterChart = useMemo(() => {
    const series = Array.isArray(data.series) ? data.series : [data.series];
    return series.some((s: any) => s?.type === 'scatter');
  }, [data.series]);

  // Check if this is a graph chart and log node/link counts
  const isGraphChart = useMemo(() => {
    const series = Array.isArray(data.series) ? data.series : [data.series];
    const graphSeries = series.find((s: any) => s?.type === 'graph') as any;
    if (graphSeries) {
      const nodeCount = (graphSeries.data as any[])?.length || 0;
      const linkCount = (graphSeries.links as any[])?.length || 0;
      if (nodeCount < 6 || linkCount < 5) {
        console.warn(`⚠️ Graph chart has insufficient data (minimum: 6 nodes, 5 links)`);
      }
      return true;
    }
    return false;
  }, [data.series]);

  const chartOption = useMemo(() => {
    // Process series to handle specific chart types
    // Check if this chart should be a line chart based on keywords (TAM, CAGR, Market Analysis)
    const titleText = (typeof titleOption === 'string' ? titleOption : (titleOption as any)?.text || '').toLowerCase();
    const isMarketData = titleText.includes('tam') ||
      titleText.includes('cagr') ||
      titleText.includes('market size') ||
      titleText.includes('market analysis') ||
      titleText.includes('market forecast') ||
      titleText.includes('projections');

    const series = Array.isArray(data.series) ? data.series : [data.series];
    const formattedSeries = series.map((s: any) => {
      // Force line type for market-related data if they are currently bars or undefined
      if (isMarketData && (s.type === 'bar' || !s.type)) {
        return {
          ...s,
          type: 'line',
          smooth: true,
          symbolSize: 8,
          lineStyle: { width: 3 }
        };
      }

      // Fix for Graph charts: ensure robust force layout settings
      if (s.type === 'graph') {
        const nodeCount = s.data?.length || 0;
        return {
          ...s,
          label: {
            ...(s.label || {}),
            show: true,
            color: '#000000', // Ensure labels are always black
          },
          layout: 'force',
          roam: true,
          draggable: true,
          force: {
            repulsion: 500, // Higher repulsion to spread nodes
            gravity: 0.08,
            edgeLength: [120, 250],
            friction: 0.6,
            layoutAnimation: true,
          },
          // Give each node a starting position in a wider circle
          data: s.data?.map((node: any, index: number) => {
            const angle = (2 * Math.PI * index) / nodeCount;
            const radius = 200; // Wider initial spread
            return {
              ...node,
              draggable: true,
              x: 400 + radius * Math.cos(angle),
              y: 250 + radius * Math.sin(angle),
              fixed: false
            };
          })
        };
      }
      return s;
    });

    // For scatter charts, override tooltip with a proper formatter function
    const tooltipConfig = isScatterChart ? {
      ...data.tooltip,
      className: 'analytics-chart-tooltip',
      trigger: 'item' as const,
      formatter: (params: any) => {
        const value = params.value;
        const name = value[2] || params.seriesName || '';

        // Robustly get axis names even if they are arrays
        const xAxis = Array.isArray(data.xAxis) ? data.xAxis[0] : data.xAxis;
        const yAxis = Array.isArray(data.yAxis) ? data.yAxis[0] : data.yAxis;

        const xAxisName = (xAxis as any)?.name || 'X';
        const yAxisName = (yAxis as any)?.name || 'Y';

        // Remove units for shorter display
        const xTitle = xAxisName.split('(')[0].trim();
        const yTitle = yAxisName.split('(')[0].trim();

        return `<b>${name}</b><br/>${xTitle}: ${value[0]}<br/>${yTitle}: ${value[1]}`;
      }
    } : {
      ...data.tooltip,
      className: 'analytics-chart-tooltip',
    };

    // Null out data for hidden series so ECharts recalculates axis scale properly
    const visibleSeries = formattedSeries.map((s: any) =>
      hiddenSeries.has(s?.name)
        ? { ...s, data: Array.isArray(s.data) ? s.data.map(() => null) : s.data }
        : s
    );

    // Check if this is a radar chart
    const isRadarChart = visibleSeries.some((s: any) => s?.type === 'radar');

    // For radar charts, extract all data point names to ensure complete legend
    const radarLegendData = isRadarChart
      ? visibleSeries
        .filter((s: any) => s?.type === 'radar')
        .flatMap((s: any) => s?.data || [])
        .map((d: any) => d?.name)
        .filter((name: string) => name) // Remove undefined/null
      : [];

    const yAxes = Array.isArray(data.yAxis)
      ? data.yAxis
      : data.yAxis ? [data.yAxis] : [];
    const hasYAxisName = yAxes.some((axis: any) => axis?.name);
    const resolvedGrid = {
      containLabel: true,
      top: hasTitle ? 40 : 20,
      bottom: 40,
      ...(data.grid || {}),
    } as any;

    if (hasYAxisName && resolvedGrid.left == null) {
      resolvedGrid.left = 90;
    }

    return {
      ...data,
      series: visibleSeries,
      // Title configuration: ensure it wraps and doesn't get cut off, remove subtext
      title: {
        ...(typeof data.title === 'object' ? data.title : { text: data.title }),
        subtext: '', // Remove subtext to keep chart clean
        textStyle: {
          ...(typeof data.title === 'object' && (data.title as any).textStyle ? (data.title as any).textStyle : {}),
          width: '90%',
          overflow: 'break',
          fontSize: 14,
        },
        left: 'center',
        top: 5,
      },
      // Fix for axis labels showing {c} instead of {value}
      xAxis: Array.isArray(data.xAxis)
        ? data.xAxis.map(x => ({
          ...x,
          axisLine: { show: true, ...((x as any).axisLine || {}) },
          axisTick: { show: true, ...((x as any).axisTick || {}) },
          axisLabel: {
            ...(x.axisLabel || {}),
            formatter: typeof x.axisLabel?.formatter === 'string'
              ? x.axisLabel.formatter.replace(/{c}/g, '{value}')
              : x.axisLabel?.formatter
          }
        }))
        : data.xAxis ? {
          ...(data.xAxis as any),
          axisLine: { show: true, ...((data.xAxis as any).axisLine || {}) },
          axisTick: { show: true, ...((data.xAxis as any).axisTick || {}) },
          axisLabel: {
            ...((data.xAxis as any).axisLabel || {}),
            formatter: typeof (data.xAxis as any).axisLabel?.formatter === 'string'
              ? (data.xAxis as any).axisLabel.formatter.replace(/{c}/g, '{value}')
              : (data.xAxis as any).axisLabel?.formatter
          }
        } : undefined,
      yAxis: Array.isArray(data.yAxis)
        ? data.yAxis.map(y => {
          const result: any = {
            ...y,
            nameLocation: 'middle' as const,
            nameGap: (y as any).nameGap ?? 80,
            axisLine: { show: true, ...((y as any).axisLine || {}) },
            axisTick: { show: true, ...((y as any).axisTick || {}) },
            axisLabel: {
              ...(y.axisLabel || {}),
              formatter: typeof y.axisLabel?.formatter === 'string'
                ? y.axisLabel.formatter.replace(/{c}/g, '{value}')
                : y.axisLabel?.formatter
            }
          };
          if (hiddenSeries.size > 0) delete result.min;
          return result;
        })
        : data.yAxis ? (() => {
          const result: any = {
            ...(data.yAxis as any),
            nameLocation: 'middle' as const,
            nameGap: (data.yAxis as any).nameGap ?? 80,
            axisLine: { show: true, ...((data.yAxis as any).axisLine || {}) },
            axisTick: { show: true, ...((data.yAxis as any).axisTick || {}) },
            axisLabel: {
              ...((data.yAxis as any).axisLabel || {}),
              formatter: typeof (data.yAxis as any).axisLabel?.formatter === 'string'
                ? (data.yAxis as any).axisLabel.formatter.replace(/{c}/g, '{value}')
                : (data.yAxis as any).axisLabel?.formatter
            }
          };
          if (hiddenSeries.size > 0) delete result.min;
          return result;
        })() : undefined,
      // Legend is rendered as custom React component; ECharts legend hidden
      legend: { show: false },
      tooltip: tooltipConfig,
      ...(isRadarChart && data.radar ? {
        radar: {
          ...(data.radar as object),
          center: ['50%', '62%'],
          radius: '45%',
          axisName: {
            color: '#333',
            overflow: 'break' as const,
            width: 80,
            fontSize: 10,
          }
        }
      } : {}),
      // Grid configuration: keep caller overrides but reserve space for axis names when missing
      grid: resolvedGrid
    };
  }, [data, isScatterChart, hiddenSeries, seriesNames]);

  useMemo(() => {
    const series = Array.isArray(data.series) ? data.series : [data.series];

    // Check for empty data
    const hasData = series.some((s: any) => {
      // Check for standard data array
      if (Array.isArray(s.data) && s.data.length > 0) return true;
      // Check for dataset source
      if (data.dataset && (data.dataset as any).source && (data.dataset as any).source.length > 0) return true;
      return false;
    });

    if (!hasData) {
      console.warn('⚠️ Chart has NO data points:', data);
    }
  }, [data]);

  const takeScreenshot = async () => {
    try {
      const chartWrapper = chartWrapperRef.current;
      if (!chartWrapper) {
        console.warn('Chart wrapper not found for screenshot');
        return;
      }

      let html2canvas = (window as any).html2canvas;

      if (!html2canvas) {
        try {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
          script.async = true;

          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });

          html2canvas = (window as any).html2canvas;
        } catch (loadError) {
          console.warn('Failed to load html2canvas:', loadError);
        }
      }

      if (html2canvas) {
        // Capture the card at its own natural (live-page) size. Forcing a
        // narrower/wider windowWidth here used to make html2canvas reflow the
        // cloned document, which triggers known html2canvas bugs where nested
        // DOM content (like the legend) gets laid out/cropped at the wrong
        // position while raster content (the ECharts <canvas>) looks fine.
        // Capturing 1:1 with no reflow avoids that class of bug entirely.
        const canvas = await html2canvas(chartWrapper, {
          backgroundColor: '#ffffff',
          scale: 4,
          useCORS: true,
          logging: false,
          width: chartWrapper.scrollWidth,
          height: chartWrapper.scrollHeight,
          onclone: (clonedDoc: Document, clonedElement: HTMLElement) => {
            clonedElement.style.backgroundColor = '#ffffff';

            let parent = clonedElement.parentElement;
            while (parent && parent !== clonedDoc.body) {
              parent.style.overflow = 'visible';
              parent.style.maxHeight = 'none';
              parent.style.height = 'auto';
              parent = parent.parentElement;
            }
          },
          ignoreElements: (element: Element) => {
            return element.classList.contains('delete-chart-btn') ||
              element.classList.contains('analytics-chart-actions');
          }
        });

        canvas.toBlob((blob: Blob | null) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // Use chart title for filename if available, otherwise default name
            const chartTitle = hasTitle && (typeof titleOption === 'string' ? titleOption : (titleOption as any)?.text)
              ? (typeof titleOption === 'string' ? titleOption : (titleOption as any).text).replace(/[^a-z0-9]/gi, '_').toLowerCase()
              : 'chart';

            link.download = `${chartTitle}-screenshot-${new Date().toISOString().split('T')[0]}.png`;
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        }, 'image/png');
      }
    } catch (error) {
      console.warn('Screenshot failed:', error);
      alert('Screenshot capture failed. Please try using your browser screenshot tool.');
    }
  };

  return (
    <div className="analytics-chart-view" ref={chartWrapperRef}>
      <div className="analytics-chart-view-wrapper" style={{ position: 'relative' }}>
        {chartId && onDelete && (
          <button
            className="delete-chart-btn"
            onClick={() => onDelete(chartId)}
          >
            Delete
          </button>
        )}

        {seriesInfo.length > 1 && (
          <div className="analytics-chart-custom-legend">
            {seriesInfo.map(({ name, color, dashed }) => {
              const isVisible = !hiddenSeries.has(name);
              return (
                <span
                  key={name}
                  className={`analytics-chart-legend-item${isVisible ? '' : ' is-hidden'}`}
                  onClick={() => toggleSeries(name)}
                  title={isVisible ? 'Click to hide' : 'Click to show'}
                >
                  <span className={`analytics-chart-legend-checkbox${isVisible ? ' is-checked' : ''}`} />
                  <span
                    className={`analytics-chart-legend-icon${dashed ? ' is-dashed' : ''}`}
                    style={{ borderBottomColor: color }}
                  />
                  <span className="analytics-chart-legend-name">{name}</span>
                </span>
              );
            })}
          </div>
        )}

        <EChart
          option={chartOption}
          loading={false}
          events={{
            click: (params) => {
              logWhenInDevMode('Chart clicked:', params);
            },
            legendselectchanged: (params) => {
              logWhenInDevMode('Legend changed:', params);
            }
          }}
          style={{ width: '100%', maxWidth: '950px', margin: '0 auto', aspectRatio: '16/9' }}
          className="analytics-chart-canvas"
        />

        <div className="analytics-chart-actions">
          <IconButton
            iconName="fullscreen"
            text="Screenshot"
            title="Take a screenshot of this chart"
            handleClick={takeScreenshot}
            color="purple"
            size="sm"
          />
        </div>
      </div>

      {divergenceNote && (
        <div className="analytics-chart-view__footer">
          <InfoMessage
            message={divergenceNote}
            icon="warning"
            className="analytics-chart-divergence-note"
          />
        </div>
      )}
    </div>
  );
};

export default AnalyticsChart;
